import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatDate, formatTime } from '../../utils/dateUtils';
import Toast from '../Toast';
import useToast from '../../hooks/useToast';

function ManageMatches() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [homeScoreInput, setHomeScoreInput] = useState('');
  const [awayScoreInput, setAwayScoreInput] = useState('');
  const [winnerOnDrawInput, setWinnerOnDrawInput] = useState('');
  const [modalError, setModalError] = useState('');
  const [submittingScore, setSubmittingScore] = useState(false);

  // Edit Match Modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editMatchData, setEditMatchData] = useState(null);
  const [editModalError, setEditModalError] = useState('');
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [teamsList, setTeamsList] = useState([]);

  const { toasts, addToast, removeToast } = useToast();

  // Auto-clear winner_on_draw when scores diverge
  useEffect(() => {
    if (homeScoreInput && awayScoreInput && parseInt(homeScoreInput, 10) !== parseInt(awayScoreInput, 10)) {
      setWinnerOnDrawInput('');
    }
  }, [homeScoreInput, awayScoreInput]);

  const isKnockout = (round) => round && round !== 'group_stage';

  const formatForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  };

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/matches', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        const teamsResponse = await axios.get('/api/admin/teams', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        const sortedMatches = response.data.sort((a, b) =>
          new Date(a.kickoff_time) - new Date(b.kickoff_time)
        );

        setMatches(sortedMatches);
        setTeamsList(teamsResponse.data);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar partidas');
        console.error('Error fetching matches:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, []);

  const handleUpdateResults = (matchId) => {
    const match = matches.find(m => m.id === matchId);
    if (!match) return;

    setSelectedMatch(match);
    setHomeScoreInput(match.home_score !== null ? match.home_score.toString() : '');
    setAwayScoreInput(match.away_score !== null ? match.away_score.toString() : '');
    setWinnerOnDrawInput(match.winner_on_draw || '');
    setModalError('');
    setIsModalOpen(true);
  };

  const closeScoreModal = () => {
    setIsModalOpen(false);
    setSelectedMatch(null);
    setHomeScoreInput('');
    setAwayScoreInput('');
    setWinnerOnDrawInput('');
    setModalError('');
  };

  const submitScoreUpdate = async (e) => {
    e.preventDefault();
    if (!selectedMatch) return;

    const homeScore = homeScoreInput;
    const awayScore = awayScoreInput;

    if (homeScore === null || awayScore === null) return;

    if (homeScore.trim() === '' || awayScore.trim() === '') {
      setModalError('Não é possível salvar o placar pela falta do valor em um dos times (ou em ambos)');
      return;
    }

    const parsedHomeScore = parseInt(homeScore, 10);
    const parsedAwayScore = parseInt(awayScore, 10);

    if (isNaN(parsedHomeScore) || isNaN(parsedAwayScore)) {
      setModalError('Placares devem ser números válidos');
      return;
    }

    // Validate winner_on_draw for knockout draws
    const knockout = isKnockout(selectedMatch.round);
    const isDrawResult = parsedHomeScore === parsedAwayScore;
    if (knockout && isDrawResult && !winnerOnDrawInput) {
      setModalError('Para empates em jogos eliminatórios, indique qual seleção avança');
      return;
    }

    const payload = {
      home_score: parsedHomeScore,
      away_score: parsedAwayScore
    };
    if (knockout && isDrawResult) {
      payload.winner_on_draw = winnerOnDrawInput;
    }

    setSubmittingScore(true);
    try {
      const response = await axios.put(`/api/admin/matches/${selectedMatch.id}/results`, payload, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setMatches(matches.map(m =>
        m.id === selectedMatch.id
          ? response.data
          : m
      ));

      addToast('Resultados atualizados com sucesso!', 'success');
      closeScoreModal();
    } catch (err) {
      setModalError('Erro ao atualizar resultados');
      console.error('Error updating match results:', err);
    } finally {
      setSubmittingScore(false);
    }
  };

  const handleEditMatch = (matchId) => {
    const match = matches.find(m => m.id === matchId);
    if (!match) return;

    setEditMatchData({
      id: match.id,
      home_team: match.home_team,
      away_team: match.away_team,
      kickoff_time: formatForInput(match.kickoff_time)
    });
    setEditModalError('');
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditMatchData(null);
    setEditModalError('');
  };

  const submitEditMatch = async (e) => {
    e.preventDefault();
    if (!editMatchData) return;

    setSubmittingEdit(true);
    try {
      const response = await axios.put(`/api/matches/${editMatchData.id}`, {
        home_team: editMatchData.home_team,
        away_team: editMatchData.away_team,
        kickoff_time: new Date(editMatchData.kickoff_time).toISOString()
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      setMatches(matches.map(m =>
        m.id === editMatchData.id
          ? response.data
          : m
      ));

      addToast('Partida atualizada com sucesso!', 'success');
      closeEditModal();
    } catch (err) {
      setEditModalError('Erro ao atualizar a partida');
      console.error('Error updating match:', err);
    } finally {
      setSubmittingEdit(false);
    }
  };

  if (loading) {
    return <div className="p-6">Carregando partidas...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-2xl font-bold mb-6 text-primary">Gerenciar Partidas</h1>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full min-w-[600px] divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Partida</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Data/Hora</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Grupo/Fase</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Placar</th>
              <th className="px-3 lg:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {matches.map((match) => (
              <tr key={match.id} className="hover:bg-gray-50">
                <td className="px-3 lg:px-6 py-4">
                  <div className="font-medium text-gray-900 break-words" title={match.home_team_pt || match.home_team}>{match.home_team_pt || match.home_team}</div>
                  <div className="text-gray-500">vs</div>
                  <div className="font-medium text-gray-900 break-words" title={match.away_team_pt || match.away_team}>{match.away_team_pt || match.away_team}</div>
                </td>
                <td className="px-3 lg:px-6 py-4">
                  <div className="text-sm text-gray-900">{formatDate(match.kickoff_time)}</div>
                  <div className="text-sm text-gray-500">{formatTime(match.kickoff_time)}</div>
                </td>
                <td className="px-3 lg:px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {match.group_stage ? `Grupo ${match.group_stage}` : ''}
                  </div>
                  <div className="text-sm text-gray-500">
                    {match.round || ''}
                  </div>
                </td>
                <td className="px-3 lg:px-6 py-4">
                  {match.status === 'completed' ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-primary">
                      {match.home_score} - {match.away_score}
                      {match.winner_on_draw && (
                        <span className="ml-1 text-orange-600">
                          ({match.winner_on_draw === 'home' ? (match.home_team_pt || match.home_team) : (match.away_team_pt || match.away_team)} avança)
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                      Não realizado
                    </span>
                  )}
                </td>
                <td className="px-3 lg:px-6 py-4 text-sm font-medium">
                  <div className="flex flex-col sm:flex-row gap-1 sm:gap-4">
                    <button
                      onClick={() => handleUpdateResults(match.id)}
                      className="bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-md min-h-11 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
                    >
                      Atualizar Placar
                    </button>
                    <button
                      onClick={() => handleEditMatch(match.id)}
                      className="bg-yellow-50 hover:bg-yellow-100 text-yellow-700 px-3 py-1.5 rounded-md min-h-11 focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
                    >
                      Editar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Score Update Modal */}
      {isModalOpen && selectedMatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black/50 backdrop-blur-sm">
          <div className="relative w-full max-w-md p-4 mx-auto">
            <div className="bg-white rounded-xl shadow-xl">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900">
                  Atualizar Placar
                </h3>
                <button
                  onClick={closeScoreModal}
                  className="text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg p-1.5 transition-colors focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={submitScoreUpdate} className="p-6">
                <div className="mb-6 text-center text-sm text-gray-500">
                  {formatDate(selectedMatch.kickoff_time)} às {formatTime(selectedMatch.kickoff_time)}
                  <br />
                  {selectedMatch.group_stage ? `Grupo ${selectedMatch.group_stage}` : ''} {selectedMatch.round || ''}
                </div>

                <div className="flex items-center justify-center gap-6 mb-6">
                  {/* Home Team */}
                  <div className="flex flex-col items-center flex-1">
                    <label className="text-sm font-semibold text-gray-700 mb-2 text-center w-full truncate" title={selectedMatch.home_team_pt || selectedMatch.home_team}>
                      {selectedMatch.home_team_pt || selectedMatch.home_team}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={homeScoreInput}
                      onChange={(e) => setHomeScoreInput(e.target.value)}
                      className="w-20 text-center text-2xl font-bold p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-shadow"
                      placeholder="0"
                      autoFocus
                    />
                  </div>

                  <span className="text-2xl font-bold text-gray-500 mt-6">X</span>

                  {/* Away Team */}
                  <div className="flex flex-col items-center flex-1">
                    <label className="text-sm font-semibold text-gray-700 mb-2 text-center w-full truncate" title={selectedMatch.away_team_pt || selectedMatch.away_team}>
                      {selectedMatch.away_team_pt || selectedMatch.away_team}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={awayScoreInput}
                      onChange={(e) => setAwayScoreInput(e.target.value)}
                      className="w-20 text-center text-2xl font-bold p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-shadow"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Winner on draw selector for knockout matches */}
                {selectedMatch && isKnockout(selectedMatch.round) &&
                  homeScoreInput && awayScoreInput &&
                  parseInt(homeScoreInput, 10) === parseInt(awayScoreInput, 10) && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                      Empate! Quem avança?
                    </label>
                    <div className="flex gap-2 justify-center">
                      <button
                        type="button"
                        className={`flex-1 max-w-[200px] px-3 py-2.5 text-sm rounded-lg border-2 font-medium transition-colors ${
                          winnerOnDrawInput === 'home'
                            ? 'bg-green-100 border-green-500 text-green-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                        onClick={() => setWinnerOnDrawInput('home')}
                      >
                        {selectedMatch.home_team_pt || selectedMatch.home_team}
                      </button>
                      <button
                        type="button"
                        className={`flex-1 max-w-[200px] px-3 py-2.5 text-sm rounded-lg border-2 font-medium transition-colors ${
                          winnerOnDrawInput === 'away'
                            ? 'bg-green-100 border-green-500 text-green-800'
                            : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                        onClick={() => setWinnerOnDrawInput('away')}
                      >
                        {selectedMatch.away_team_pt || selectedMatch.away_team}
                      </button>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {modalError && (
                  <div className="mb-6 p-3 bg-red-50 text-red-700 text-sm rounded-lg flex items-start gap-2">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{modalError}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 mt-8">
                  <button
                    type="button"
                    onClick={closeScoreModal}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-100 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={submittingScore}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark focus:ring-4 focus:ring-green-300 transition-colors disabled:opacity-50"
                  >
                    {submittingScore ? 'Salvando...' : 'Salvar Resultado'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Match Modal */}
      {isEditModalOpen && editMatchData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-black/50 backdrop-blur-sm">
          <div className="relative w-full max-w-md p-4 mx-auto">
            <div className="bg-white rounded-xl shadow-xl">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-900">
                  Editar Partida
                </h3>
                <button
                  onClick={closeEditModal}
                  className="text-gray-400 hover:text-gray-500 hover:bg-gray-100 rounded-lg p-1.5 transition-colors focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <form onSubmit={submitEditMatch} className="p-6">
                <div className="flex gap-4 mb-6">
                  <div className="flex-1">
                    <label htmlFor="home_team" className="sr-only">
                      Seleção 1
                    </label>
                    <input
                      type="text"
                      list="teamsList"
                      id="home_team"
                      value={editMatchData.home_team}
                      onChange={(e) => setEditMatchData({ ...editMatchData, home_team: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-shadow"
                      placeholder="Seleção 1"
                      required
                    />
                  </div>
                  <div className="flex items-center pt-2 text-gray-500 font-bold">X</div>
                  <div className="flex-1">
                    <label htmlFor="away_team" className="sr-only">
                      Seleção 2
                    </label>
                    <input
                      type="text"
                      list="teamsList"
                      id="away_team"
                      value={editMatchData.away_team}
                      onChange={(e) => setEditMatchData({ ...editMatchData, away_team: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-shadow"
                      placeholder="Seleção 2"
                      required
                    />
                  </div>
                </div>

                <datalist id="teamsList">
                  {teamsList.map(team => (
                    <option key={team.id} value={team.name_en}>
                      {team.name_pt ? `${team.name_en} (${team.name_pt})` : team.name_en}
                    </option>
                  ))}
                </datalist>

                <div className="mb-6">
                  <label htmlFor="kickoff_time" className="block text-sm font-medium text-gray-700 mb-2">
                    Data e Hora do Jogo
                  </label>
                  <input
                    type="datetime-local"
                    id="kickoff_time"
                    value={editMatchData.kickoff_time}
                    onChange={(e) => setEditMatchData({ ...editMatchData, kickoff_time: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-shadow"
                    required
                  />
                </div>

                {/* Error Message */}
                {editModalError && (
                  <div className="mb-6 p-3 bg-red-50 text-red-700 text-sm rounded-lg flex items-start gap-2">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{editModalError}</span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 mt-8">
                  <button
                    type="button"
                    onClick={closeEditModal}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-4 focus:ring-gray-100 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={submittingEdit}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark focus:ring-4 focus:ring-green-300 transition-colors disabled:opacity-50"
                  >
                    {submittingEdit ? 'Salvando...' : 'Salvar Alteração'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Toast toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default ManageMatches;
